import { useContext, useMemo } from "react";
import { useReactiveVar } from "@apollo/client";
import { CompanyAccess, PermissionScope } from "@me-team/host/main/graphql/types";
import { currentBranchEmployeeIdVar } from "@me-team/host/main/globalVar/state";
import { AccessContext } from "@me-team/host/src/components/App/App";
import AccessService from "@me-team/host/services/AccessService";
import { useModulesAccess } from "@me-team/host/src/utils/useModulesAccess";
import { useRoleAccess } from "@me-team/host/src/utils/useRoleAccess";
import { AllModulesTagName } from "../../../utils/enums";

type EditingEmployeePageAccessMode = "forbidden" | "view" | "edit";
type EditingEmployeeModalAccessMode = "hidden" | "view" | "edit";

interface UseEditingEmployeeAccessParams {
    employeeId?: number | string | null;
}

interface EditingEmployeeAccess {
    isEditingOwnProfile: boolean;
    employeeActionScope: PermissionScope;
    isEmployeePageAccessPending: boolean;
    employeePageAccessMode: EditingEmployeePageAccessMode;
    isEmployeePageReadOnly: boolean;
    canManageAccessSettings: boolean;
    isAccessSettingsReadOnly: boolean;
    salaryAccessMode: EditingEmployeeModalAccessMode;
    bookingAccessMode: EditingEmployeeModalAccessMode;
    canOpenEmployeeServices: boolean;
    canOpenSchedule: boolean;
    canDismissEmployee: boolean;
    hasEmployeePageActions: boolean;
}

export const useEditingEmployeeAccess = ({
    employeeId,
}: UseEditingEmployeeAccessParams = {}): EditingEmployeeAccess => {
    const currentBranchEmployeeId = useReactiveVar(currentBranchEmployeeIdVar);
    const { isReady: isAccessContextReady } = useContext(AccessContext);
    const accessService = AccessService.getInstance();

    const isEditingOwnProfile = Boolean(
        currentBranchEmployeeId && employeeId && currentBranchEmployeeId === Number(employeeId)
    );

    const canViewEmployeePageAll = useRoleAccess(CompanyAccess.EmployeeView, { scope: PermissionScope.All });
    const canViewEmployeePageSelf = useRoleAccess(CompanyAccess.EmployeeView, { scope: PermissionScope.Self });
    const canEditEmployeePageAll = useRoleAccess(CompanyAccess.EmployeeEdit, { scope: PermissionScope.All });
    const canEditEmployeePageSelf = useRoleAccess(CompanyAccess.EmployeeEdit, { scope: PermissionScope.Self });
    const canManageAccessSettings = useRoleAccess(CompanyAccess.AccessManageBranchEmployees);

    const employeeActionScope = isEditingOwnProfile ? PermissionScope.Self : PermissionScope.All;

    const canViewSalarySettings = useRoleAccess(CompanyAccess.SalarySettingsView, { scope: employeeActionScope });
    const canEditSalarySettings = useRoleAccess(CompanyAccess.SalarySettingsEdit, { scope: employeeActionScope });
    const canOpenEmployeeServices = useRoleAccess(CompanyAccess.EmployeeServicesView, { scope: employeeActionScope });
    const canOpenSchedule = useRoleAccess(CompanyAccess.ScheduleCalendarView, { scope: employeeActionScope });
    const canViewBookingSettings = useRoleAccess(CompanyAccess.EmployeeBookingSettingsView, {
        scope: employeeActionScope,
    });
    const canEditBookingSettings = useRoleAccess(CompanyAccess.EmployeeBookingSettingsEdit, {
        scope: employeeActionScope,
    });
    const canDismissEmployee = useRoleAccess(CompanyAccess.EmployeeDismiss, { scope: employeeActionScope });

    const hasSalaryModuleAccess = useModulesAccess([AllModulesTagName.SALARY]);
    const hasServicesModuleAccess = useModulesAccess([AllModulesTagName.SERVICES]);
    const hasBookingModuleAccess = useModulesAccess([AllModulesTagName.BOOKING]);
    const hasWorkScheduleModuleAccess = useModulesAccess([AllModulesTagName.WORK_SCHEDULE]);

    const canViewEmployeePage = canViewEmployeePageAll || (canViewEmployeePageSelf && isEditingOwnProfile);
    const canEditEmployeePage = canEditEmployeePageAll || (canEditEmployeePageSelf && isEditingOwnProfile);
    const isEmployeePageAccessPending =
        !isAccessContextReady || !accessService.isRoleAccessReady() || accessService.isRoleAccessLoading();
    const employeePageAccessMode = canEditEmployeePage ? "edit" : canViewEmployeePage ? "view" : "forbidden";
    const isEmployeePageReadOnly = employeePageAccessMode === "view";
    const isAccessSettingsReadOnly = isEmployeePageReadOnly || !canManageAccessSettings;
    const salaryAccessMode = canEditSalarySettings ? "edit" : canViewSalarySettings ? "view" : "hidden";
    const bookingAccessMode = canEditBookingSettings ? "edit" : canViewBookingSettings ? "view" : "hidden";
    const hasEmployeePageActions =
        (hasSalaryModuleAccess && salaryAccessMode !== "hidden") ||
        (hasServicesModuleAccess && canOpenEmployeeServices) ||
        (hasWorkScheduleModuleAccess && canOpenSchedule) ||
        (hasBookingModuleAccess && bookingAccessMode !== "hidden") ||
        canDismissEmployee;

    return useMemo(
        () => ({
            isEditingOwnProfile,
            employeeActionScope,
            isEmployeePageAccessPending,
            employeePageAccessMode,
            isEmployeePageReadOnly,
            canManageAccessSettings,
            isAccessSettingsReadOnly,
            salaryAccessMode,
            bookingAccessMode,
            canOpenEmployeeServices,
            canOpenSchedule,
            canDismissEmployee,
            hasEmployeePageActions,
        }),
        [
            bookingAccessMode,
            canDismissEmployee,
            canManageAccessSettings,
            canOpenEmployeeServices,
            canOpenSchedule,
            employeeActionScope,
            employeePageAccessMode,
            hasEmployeePageActions,
            isEditingOwnProfile,
            isAccessSettingsReadOnly,
            isEmployeePageAccessPending,
            isEmployeePageReadOnly,
            salaryAccessMode,
        ]
    );
};
